import React, { useState, FormEvent, useEffect } from 'react';
import { Suggest, ItemRenderer, ItemPredicate } from '@blueprintjs/select';
import { FormGroup, InputGroup, Button, Checkbox, HTMLTable, MenuItem, Spinner } from "@blueprintjs/core";
import { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';

import { VendorActivityList, VendorActivity, VendorActivityInvoice, VendorActivityInvoiceItem, Environment, Vendor } from './Types'
import * as utils from "./utils";

interface PayableAgingReportProps {
    svc: AxiosInstance,
    currentEnv?: Environment,
}

function PayableAgingReport({ svc, currentEnv = {} as Environment }: PayableAgingReportProps) {
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState({} as VendorActivityList)
    const [totals, setTotals] = useState({} as Map<string, number[]>)
    const [grandTotals, setGrandTotals] = useState([0,0,0,0,0,0])
    const [vendList, setVendList] = useState([] as Vendor[])

    let date = new Date(), y = date.getFullYear(), m = date.getMonth()
    const [fromVend, setFromVend] = useState({} as Vendor)
    const [toVend, setToVend] = useState({} as Vendor)
    //const [from, setFrom] = useState(utils.formatDate(new Date(y, m, 1)))
    const [from, setFrom] = useState(utils.formatDate(new Date(currentEnv.startOfYear)))
    const [to, setTo] = useState(utils.formatDate(date)) // new Date(y, m+1, 0)
    const [paid, setPaid] = useState(false)

    const refreshVendList = async () => {
        if ("envId" in currentEnv) {
            const res = await svc.post('/vendorlist');
            //console.log("refreshVendList", res.data.data)
            setVendList([{vendCode: "Any", vendName: "Any"} as Vendor ].concat(res.data.data))
        }
    }

    useEffect(() => {
        refreshVendList();
    }, [svc, currentEnv])
    
    const vendRenderer: ItemRenderer<Vendor> = (vend, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }
        const text = `${vend.vendName}`;
        return (
            <MenuItem
                active={modifiers.active}
                disabled={modifiers.disabled}
                label={vend.vendCode}
                key={vend.vendCode}
                onClick={handleClick}
                text={highlightText(text, query)}
            />
        );
    };

    const vendPredicate: ItemPredicate<Vendor> = (query, vend, _index, exactMatch) => {
        const normalizedText = `${vend.vendCode} - ${vend.vendName}`.toLowerCase();
        const normalizedQuery = query.toLowerCase();

        if (exactMatch) {
            return normalizedText === normalizedQuery;
        } else {
            return normalizedText.indexOf(normalizedQuery) >= 0;
        }
    };

    const vendValueRenderer = (vend: Vendor) => {
        return vend.vendCode
    }

    function highlightText(text: string, query: string) {
        let lastIndex = 0;
        const words = query
            .split(/\s+/)
            .filter(word => word.length > 0)
            .map(escapeRegExpChars);
        if (words.length === 0) {
            return [text];
        }
        const regexp = new RegExp(words.join("|"), "gi");
        const tokens: React.ReactNode[] = [];
        while (true) {
            const match = regexp.exec(text);
            if (!match) {
                break;
            }
            const length = match[0].length;
            const before = text.slice(lastIndex, regexp.lastIndex - length);
            if (before.length > 0) {
                tokens.push(before);
            }
            lastIndex = regexp.lastIndex;
            tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
        }
        const rest = text.slice(lastIndex);
        if (rest.length > 0) {
            tokens.push(rest);
        }
        return tokens;
    }

    function escapeRegExpChars(text: string) {
        //return text.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
        return text.replace(/([.*+?^=!:${}()|\\])/g, "\\$1");
    }

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            setIsLoading(true)
            const res = await svc.post('/vendoractivity',
                {
                    fromVendCode: fromVend.vendCode === "Any" ? "" : fromVend.vendCode,
                    toVendCode: toVend.vendCode === "Any" ? "" : toVend.vendCode,
                    fromDate: from,
                    toDate: to,
                    includesPaid: paid,
                });

            setIsLoading(false)
            //console.log(res.data.data.data.length)
            let newTotals = new Map<string, number[]>()
            let newGrandTotals = [0, 0, 0, 0, 0, 0]
            for (let i = 0; i < res.data.data.data.length; i++) {
                let vend = res.data.data.data[i] as VendorActivity
                let vendTotals = [0, 0, 0, 0, 0, 0]
                for (let j = 0; j < vend.invoices.length; j++) {
                    let inv = vend.invoices[j] as VendorActivityInvoice
                    vendTotals[0] += inv.outstanding
                    vendTotals[1] += utils.isInPeriod(new Date(inv.dueDate), new Date(res.data.data.toDate), -99999, 0) ? inv.outstanding : 0
                    vendTotals[2] += utils.isInPeriod(new Date(inv.dueDate), new Date(res.data.data.toDate), 1, 30) ? inv.outstanding : 0
                    vendTotals[3] += utils.isInPeriod(new Date(inv.dueDate), new Date(res.data.data.toDate), 31, 60) ? inv.outstanding : 0
                    vendTotals[4] += utils.isInPeriod(new Date(inv.dueDate), new Date(res.data.data.toDate), 61, 90) ? inv.outstanding : 0
                    vendTotals[5] += utils.isInPeriod(new Date(inv.dueDate), new Date(res.data.data.toDate), 91, 99999) ? inv.outstanding : 0
                    /*
                    for (let k = 0; k < inv.items.length; k++) {
                        let item = inv.items[k]
                    }
                    */
                }
                newGrandTotals[0] += vendTotals[0]
                newGrandTotals[1] += vendTotals[1]
                newGrandTotals[2] += vendTotals[2]
                newGrandTotals[3] += vendTotals[3]
                newGrandTotals[4] += vendTotals[4]
                newGrandTotals[5] += vendTotals[5]
                newTotals.set(vend.vendCode, vendTotals)
                //console.log(vend.vendCode, vendTotals)
            }
            setTotals(newTotals)
            setGrandTotals(newGrandTotals)
            setData(res.data.data)
        } catch (error) {
            utils.showError(error)
        }
    }

    let pageMaxWidth = "1310px"
    let pageTitle = t('payable_aging_report')
    let list = <div className="Print-Align-C Print-90-Percent" style={{maxWidth: pageMaxWidth}}>
        <div className="Spacing-V-16" />
        <div className="No-Screen">
            <div className="Row" style={{maxWidth: pageMaxWidth}}>
                <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{currentEnv.companyName}</div>
            </div>
            <div className="Spacing-V-8" />
            {currentEnv.companyAddress && currentEnv.companyAddress.split('\n').map((line) =>
                <div className="Row" style={{maxWidth: pageMaxWidth}}>
                    <div className="Col Align-C">{line}</div>
                </div>
            )}
        </div>
        <div className="Spacing-V-8" />
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{pageTitle}</div>
        </div>
        {isLoading && <Spinner></Spinner>}
        <div className="Spacing-V-8" />
        {/*
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "14px", fontWeight: 600}}>Account From: {data.fromAcCode === "" ? "Any" : data.fromAcCode}<div className="Spacing-H-8"/>To: {data.toAcCode === "" ? "Any" : data.toAcCode}</div>
        </div>
        */}
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Col-H Align-C" style={{fontSize: "14px", fontWeight: 600}}>{t('from')}: {data.fromDate}<div className="Spacing-H-8"/>{t('to')}: {data.toDate}</div>
        </div>
        <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed>
            <thead>
                <tr>
                    <th className="TCol-InvoiceNo">{t('invoice_no')}</th>
                    <th className="TCol-Date">{t('date')}</th>
                    <th className="TCol-Date">{t('due_date')}</th>
                    <th className="TCol-TxType">{t('type')}</th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('amount')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('undue')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('<= 30 days')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('31 - 60 days')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('61 - 90 days')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('> 90 days')}</div></th>
                </tr>
            </thead>
            <tbody>
                {data.data && data.data.map((vend: VendorActivity) => <>
                    <tr>
                        <td className="TCol-InvoiceNo"><b>{vend.vendCode}</b></td>
                        <td className="TCol-Date" colSpan={3}><b>{vend.vendName}</b></td>
                        {/*
                        <td className="TCol-Date"></td>
                        <td className="TCol-TxType"></td>
                        */}
                        <td className="TCol-ACBalance"></td>
                        <td className="TCol-ACBalance"></td>
                        <td className="TCol-ACBalance"></td>
                        <td className="TCol-ACBalance"></td>
                        <td className="TCol-ACBalance"></td>
                        <td className="TCol-ACBalance"></td>
                    </tr>
                    {vend.invoices?.map((inv: VendorActivityInvoice) => <>
                    {/* <tr>
                        <td className="TCol-InvoiceNo">{inv.invNo}</td>
                        <td className="TCol-Date">{inv.invDate}</td>
                        <td className="TCol-Date">{inv.dueDate}</td>
                        <td className="TCol-TxType">{inv.txType}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(inv.amount)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{inv.items.length === 0 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), -99999, 0) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{inv.items.length === 0 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 1, 30) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{inv.items.length === 0 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 31, 60) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{inv.items.length === 0 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 61, 90) && utils.formatMoney(inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{inv.items.length === 0 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 91, 99999) && utils.formatMoney(inv.outstanding)}</div></td>
                    </tr> */}
                    {inv.items.map((item: VendorActivityInvoiceItem, i: number) =>
                    <tr>
                        <td className="TCol-InvoiceNo">{i === 0 && inv.invNo}</td>
                        <td className="TCol-Date">{item.date}</td>
                        <td className="TCol-Date">{item.dueDate}</td>
                        <td className="TCol-TxType">{item.txType}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(-item.amount)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{i === inv.items.length-1 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), -99999, 0) && utils.formatMoney(-inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{i === inv.items.length-1 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 1, 30) && utils.formatMoney(-inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{i === inv.items.length-1 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 31, 60) && utils.formatMoney(-inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{i === inv.items.length-1 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 61, 90) && utils.formatMoney(-inv.outstanding)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{i === inv.items.length-1 && inv.outstanding !== 0 && utils.isInPeriod(new Date(inv.dueDate), new Date(data.toDate), 91, 99999) && utils.formatMoney(-inv.outstanding)}</div></td>
                    </tr>
                    )}
                    </>)}
                    <tr>
                        <td className="TCol-InvoiceNo"></td>
                        <td className="TCol-Date" colSpan={2}><b>{t('TOTALS')}</b></td>
                        {/*<td className="TCol-Date"></td>*/}
                        <td className="TCol-TxType"></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(vend.vendCode) && utils.formatMoney(-totals.get(vend.vendCode)![0])}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(vend.vendCode) && utils.formatMoney(-totals.get(vend.vendCode)![1])}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(vend.vendCode) && utils.formatMoney(-totals.get(vend.vendCode)![2])}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(vend.vendCode) && utils.formatMoney(-totals.get(vend.vendCode)![3])}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(vend.vendCode) && utils.formatMoney(-totals.get(vend.vendCode)![4])}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{totals.get(vend.vendCode) && utils.formatMoney(-totals.get(vend.vendCode)![5])}</b></div></td>
                    </tr>
                    </>
                )}
                <tr>
                    <td className="TCol-InvoiceNo"></td>
                    <td className="TCol-Date" colSpan={2}><b>{t('GRAND_TOTALS')}</b></td>
                    {/*<td className="TCol-Date"></td>*/}
                    <td className="TCol-TxType"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-grandTotals[0])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-grandTotals[1])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-grandTotals[2])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-grandTotals[3])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-grandTotals[4])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-grandTotals[5])}</b></div></td>
                </tr>
            </tbody>
        </HTMLTable>
    </div>

    return (
        <>
            <form className="No-Print" onSubmit={onSubmit}>
                <div className="Row">
                    <FormGroup className="Col-1-1" label={t('from')} labelFor="fromAcCode">
                        {/*<InputGroup id="fromAcCode" value={fromAcCode} onChange={utils.handleStringChange(s => setFromAcCode(s))} />*/}
                        <Suggest
                                inputProps={{ placeholder: t('vendor'), onKeyDown: (e) => {e.key === "Enter" && e.preventDefault()} }}
                                selectedItem={fromVend}
                                items={vendList}
                                itemRenderer={vendRenderer}
                                itemPredicate={vendPredicate}
                                inputValueRenderer={vendValueRenderer}
                                resetOnClose={true}
                                onItemSelect={(vend) => {
                                    setFromVend(vend)
                                }}
                            />
                    </FormGroup>
                    <FormGroup className="Col-2-1" label={t('to')} labelFor="toAcCode">
                        {/*<InputGroup id="toAcCode" value={toAcCode} onChange={utils.handleStringChange(s => setToAcCode(s))} />*/}
                        <Suggest
                                inputProps={{ placeholder: t('vendor'), onKeyDown: (e) => {e.key === "Enter" && e.preventDefault()} }}
                                selectedItem={toVend}
                                items={vendList}
                                itemRenderer={vendRenderer}
                                itemPredicate={vendPredicate}
                                inputValueRenderer={vendValueRenderer}
                                resetOnClose={true}
                                onItemSelect={(vend) => {
                                    setToVend(vend)
                                }}
                            />
                    </FormGroup>
                    <FormGroup className="Col-3-1" label={t('from')} labelFor="from">
                        <InputGroup id="from" value={from} type="date" onChange={utils.handleStringChange(s => setFrom(s))} />
                    </FormGroup>
                    <FormGroup className="Col-4-1" label={t('to')} labelFor="to">
                        <InputGroup id="to" value={to} type="date" onChange={utils.handleStringChange(s => setTo(s))} />
                    </FormGroup>
                    <Button className="Col-5-1 Button-LabeledFormGroup" icon="desktop" type="submit" >{t('preview')}</Button>
                </div>
                <div className="Row">
                    {/* Checkbox-LabeledFormGroup */}
                    <Checkbox className="Col-1-1" label={t('includes_paid')} checked={paid} onChange={utils.handleBooleanChange(v => setPaid(v))} />
                </div>
            </form>
            {list}
        </>
    );
}

export default PayableAgingReport